import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type React from "react";
import { useCallback, useState } from "react";
import { EmptyState, VisuallyHiddenInput } from "@/components/ui";
import { useApi } from "../../hooks/use-api";
import { useApiMutation } from "../../hooks/use-api-mutation";
import { useApiQuery } from "../../hooks/use-api-query";
import { type AppContext, AppContextType } from "../../types";

interface ContextFormData {
  context_type: AppContextType;
  context_raw_text?: string;
  file_name?: string;
  file?: File;
  app_config_id?: string;
}

export const ContextTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contextToDelete, setContextToDelete] = useState<AppContext | null>(null);
  const [editingContext, setEditingContext] = useState<AppContext | null>(null);
  const [formData, setFormData] = useState<ContextFormData>({
    context_type: AppContextType.TEXT,
  });

  // Fetch contexts
  const { data: contexts, isLoading, refetch } = useApiQuery<AppContext[]>("/app-contexts");

  // Direct API access for file uploads
  const { callApi } = useApi<AppContext>();

  // Create context mutation - use the file upload endpoint
  const { mutate: createContext, isPending: isCreating } = useMutation({
    mutationFn: async (formData: FormData) => {
      return callApi("/app-contexts-upload", {
        method: "POST",
        body: formData,
      });
    },
    onSuccess: () => {
      enqueueSnackbar("Context created successfully", { variant: "success" });
      handleDialogClose();
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to create context: ${error.message}`, {
        variant: "error",
      });
    },
  });

  // Create text context mutation
  const { mutate: createTextContext, isPending: isCreatingText } = useApiMutation<
    {
      context_type: string;
      context_raw_text: string;
      app_config_id?: string;
    },
    AppContext
  >("POST", "/app-contexts", {
    onSuccess: () => {
      enqueueSnackbar("Context created successfully", { variant: "success" });
      handleDialogClose();
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to create context: ${error.message}`, {
        variant: "error",
      });
    },
  });

  // Update context mutation
  const { mutate: updateContext, isPending: isUpdating } = useApiMutation<
    Partial<AppContext>,
    AppContext
  >("PATCH", editingContext ? `/app-contexts/${editingContext.id}` : "", {
    onSuccess: () => {
      enqueueSnackbar("Context updated successfully", { variant: "success" });
      handleDialogClose();
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update context: ${error.message}`, {
        variant: "error",
      });
    },
  });

  // Delete context mutation
  const { mutate: deleteContext, isPending: isDeletingContext } = useApiMutation<void, void>(
    "DELETE",
    "",
    {
      onSuccess: () => {
        enqueueSnackbar("Context deleted successfully", { variant: "success" });
        setDeleteDialogOpen(false);
        setContextToDelete(null);
        refetch();
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to delete context: ${error.message}`, {
          variant: "error",
        });
      },
    },
  );

  // Get presigned URL for file download
  const { mutate: getPresignedUrl, isPending: isGeneratingUrl } = useApiMutation<void, AppContext>(
    "GET",
    "",
    {
      onSuccess: (data) => {
        if (data.file_path) {
          window.open(data.file_path, "_blank");
        }
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to download file: ${error.message}`, {
          variant: "error",
        });
      },
    },
  );

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingContext(null);
    setFormData({ context_type: AppContextType.TEXT });
  };

  const handleCreate = () => {
    setEditingContext(null);
    setFormData({ context_type: AppContextType.TEXT });
    setDialogOpen(true);
  };

  const handleEdit = (context: AppContext) => {
    setEditingContext(context);
    setFormData({
      context_type: context.context_type,
      context_raw_text: context.context_raw_text || "",
      file_name: context.file_name || "",
      app_config_id: context.app_config_id,
    });
    setDialogOpen(true);
  };

  const handleDelete = (context: AppContext) => {
    setContextToDelete(context);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = useCallback(() => {
    if (contextToDelete) {
      deleteContext({
        _url: `/app-contexts/${contextToDelete.id}`,
      });
    }
  }, [contextToDelete, deleteContext]);

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setContextToDelete(null);
  };

  const handleDownload = (contextId: string) => {
    getPresignedUrl({
      _url: `/app-contexts/${contextId}`,
    });
  };

  const handleSubmit = () => {
    if (editingContext) {
      // Update existing context
      const updateData = {
        context_type: formData.context_type,
        context_raw_text: formData.context_raw_text,
        file_name: formData.file_name,
        app_config_id: formData.app_config_id,
      };

      updateContext(updateData);
    } else {
      // Create new context
      if (formData.context_type === AppContextType.TEXT) {
        // Use regular POST for text contexts
        createTextContext({
          context_type: formData.context_type,
          context_raw_text: formData.context_raw_text || "",
          app_config_id: formData.app_config_id,
        });
      } else if (formData.file) {
        // Use form data for file uploads
        const submitData = new FormData();
        submitData.append("file", formData.file);
        submitData.append("context_type", formData.context_type);

        if (formData.app_config_id) {
          submitData.append("app_config_id", formData.app_config_id);
        }

        createContext(submitData);
      }
    }
  };

  const getFileExtensions = (contextType: AppContextType): string => {
    switch (contextType) {
      case AppContextType.DOCUMENT:
        return ".pdf,.txt,.md,.doc,.docx";
      case AppContextType.JSON:
        return ".json";
      default:
        return "";
    }
  };

  const isFormValid = () => {
    if (formData.context_type === AppContextType.TEXT) {
      return formData.context_raw_text?.trim();
    }
    return formData.file || editingContext;
  };

  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          spacing={2}
        >
          <Stack spacing={1}>
            <Typography variant="h5" component="div">
              Your Contexts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage text and document contexts for your application. This will improve greatly the
              accuracy of our AI agents as they will have access to information about your app.
            </Typography>
          </Stack>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{ minWidth: "160px", height: "32px" }}
          >
            New Context
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {isLoading ? (
          <Typography>Loading contexts...</Typography>
        ) : (
          <Stack spacing={2} divider={<Divider />}>
            {contexts?.map((context) => (
              <Stack direction="column" key={context.id} spacing={2}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="column" spacing={0} alignItems="flex-start">
                    <Typography variant="h6" component="div">
                      {context.file_name
                        ? context.file_name
                        : context.context_type === AppContextType.TEXT
                          ? "Text Context"
                          : "Document Context"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Type: {context.context_type}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={0.5} alignItems="flex-start">
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEdit(context)}
                        loading={isUpdating}
                        disabled={isUpdating}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {context.context_type !== AppContextType.TEXT && (
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          color="inherit"
                          onClick={() => handleDownload(context.id)}
                          loading={isGeneratingUrl}
                          disabled={isGeneratingUrl}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(context)}
                        loading={isDeletingContext}
                        disabled={isDeletingContext}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                {context.context_raw_text && (
                  <Accordion
                    sx={{
                      mt: 1,
                      boxShadow: "none",
                      "&:before": { display: "none" },
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        minHeight: "auto",
                        "&.Mui-expanded": { minHeight: "auto" },
                        "& .MuiAccordionSummary-content": {
                          margin: "8px 0",
                          "&.Mui-expanded": { margin: "8px 0" },
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <Typography variant="body2">
                          {context.context_raw_text?.split("\n")?.[0]?.substring(0, 55)}
                          {context.context_raw_text &&
                            context.context_raw_text.length > 55 &&
                            "..."}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {context.context_raw_text &&
                            `${context.context_raw_text.split(" ").length} words`}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {(context.context_raw_text ?? "").substring(0, 12000) ||
                          "No text available."}
                        {context.context_raw_text?.length > 12000 && "..."}
                      </Typography>
                      {context.context_raw_text?.length > 12000 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          (Truncated to 12,000 characters)
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                )}
              </Stack>
            ))}
            {(!contexts || contexts.length === 0) && (
              <EmptyState message='No contexts found. Click "Add Context" to create your first context.' />
            )}
          </Stack>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>{editingContext ? "Edit Context" : "Create New Context"}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Context Type</InputLabel>
                <Select
                  label="Context Type"
                  value={formData.context_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      context_type: e.target.value as AppContextType,
                    })
                  }
                  disabled={!!editingContext}
                >
                  <MenuItem value={AppContextType.TEXT}>Text</MenuItem>
                  <MenuItem value={AppContextType.DOCUMENT}>Document</MenuItem>
                  {/* <MenuItem value={AppContextType.JSON}>JSON</MenuItem> */}
                </Select>
              </FormControl>

              <TextField
                label="Name"
                fullWidth
                value={formData.file_name || ""}
                onChange={(e) => setFormData({ ...formData, file_name: e.target.value })}
                placeholder="Enter name..."
                helperText="You can update the display name for this context"
              />

              {(formData.context_type === AppContextType.TEXT ||
                (editingContext &&
                  editingContext.context_type !== AppContextType.TEXT &&
                  !!formData.context_raw_text)) && (
                <TextField
                  label="Text"
                  placeholder="Enter your context text here..."
                  multiline
                  rows={20}
                  maxRows={20}
                  fullWidth
                  value={formData.context_raw_text || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      context_raw_text: e.target.value,
                    })
                  }
                />
              )}

              {!editingContext && formData.context_type !== AppContextType.TEXT && (
                <Stack spacing={1} alignItems="center" mt={2}>
                  <Button
                    component="label"
                    variant="contained"
                    tabIndex={-1}
                    startIcon={<CloudUploadIcon />}
                    sx={{ maxWidth: "200px" }}
                  >
                    Upload
                    <VisuallyHiddenInput
                      type="file"
                      accept={getFileExtensions(formData.context_type)}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          file: e.target.files?.[0],
                        })
                      }
                      style={{ width: "100%" }}
                      multiple
                    />
                  </Button>
                  {formData.file && (
                    <Typography variant="body2">Selected file: {formData.file.name}</Typography>
                  )}
                  {formData.context_type === AppContextType.DOCUMENT && (
                    <Typography variant="caption" color="text.secondary">
                      Allowed formats: PDF, TXT, MD, DOC, DOCX
                    </Typography>
                  )}
                  {formData.context_type === AppContextType.JSON && (
                    <Typography variant="caption" color="text.secondary">
                      Allowed formats: JSON
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="error" variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="outlined"
              disabled={!isFormValid() || isCreating || isCreatingText || isUpdating}
              loading={isCreating || isCreatingText || isUpdating}
            >
              {editingContext ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">Delete Context</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete this context? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={isDeletingContext}
            >
              {isDeletingContext ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};
